import React from 'react';
import * as _ from 'lodash';
import Helmet from 'react-helmet';
import Joi from 'joi-browser';
// utilities
import { HEADER_CONSTANTS } from '../utilities/constants';
import dialogLoader from '../utilities/dialogLoader';
import _slideLoader from '../utilities/slideLoader';
import { validationByJoi } from '../utilities/commonValidation';
import { messageUtility } from '../utilities/messageUtility';
import regex from '../utilities/validateRegex';
// components
import { ConferencePageHeader } from '../components/PageHeader';
import { CloseEditView } from '../components/CloseEditView';
import {
  FormList,
  FormListItem,
  FormElement,
} from '../components/CommonFormComponents';
import { RadioButtonIcon } from '../components/icons/RadioButtonIcon';
import { Description } from '../components/Description';
import { FormInformationDialog } from '../components/FormInformationDialog';
import { TrashIcon } from '../components/icons/TrashIcon';
// services
import SessionStorageService from '../services/utilities/SessionStorageService';
import { LwApiSettings } from '../services/LwApiSettings';

/**
 * LINE WORKS 連携情報登録/詳細・編集
 *
 * @export
 * @class LwApiSettingRegister
 * @extends {React.Component}
 */
class LwApiSettingRegister extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validate: {
        invalid: true, // 入力内容エラー
        messages: {}, // エラー内容
      },
      formValues: {
        client_id: '',
        client_secret: '',
        service_account: '',
        private_key: '',
        private_key_file_name: '',
        domain_id: '',
        sso_use_flg: false,
      },
    };

    this.events = {
      form: {
        handleInput: this.handleInput,
        submit: this.submit, // 登録or更新
        validate: this.validate, // バリデーション
        selectPrivateKeyFile: this.selectPrivateKeyFile, // 認証キーファイル選択
        deletePrivateKeyFile: this.deletePrivateKeyFile, // 認証キーファイル削除
        checkSsoUseFlg: this.checkSsoUseFlg, // SSOチェック
      },
    };

    this.schema = {
      client_id: Joi.string()
        .required()
        .max(30),
      client_secret: Joi.string()
        .required()
        .max(30),
      service_account: Joi.string()
        .required()
        .regex(regex.pattern.lw_service_account, {
          name: 'lw_service_account',
        }),
      private_key: Joi.string().required(),
      private_key_file_name: Joi.string().regex(
        regex.pattern.lw_private_key_file_name,
        {
          name: 'lw_private_key_file_name',
        }
      ),
      domain_id: Joi.string().regex(regex.pattern.integerNoSign, {
        name: 'integerNoSign',
      }),
    };

    this.submitRef = React.createRef(); // 登録ボタン要素
    this.updatingFlag = false; // 連打防止フラグ

    this.lwApiSettingId =
      Number(props.view.state.id) > 0 ? props.view.state.id : null; // LINE WORKS API設定ID
  }

  componentDidMount() {
    this.lwApiSettingsShow();
  }

  /**
   * @description LINE WORKS API設定詳細取得API
   */
  lwApiSettingsShow = () => {
    if (_.isNull(this.lwApiSettingId)) {
      return;
    }
    const fieldId = SessionStorageService.getFieldId();
    LwApiSettings.show(fieldId, this.lwApiSettingId).then(res => {
      if (!res.result || !_.has(res.data.entities, 'lw_api_setting')) {
        return;
      }
      // レスポンスをstateに設定
      const lwApiSetting = res.data.entities.lw_api_setting;
      lwApiSetting['domain_id'] = String(lwApiSetting['domain_id']);
      this.setState({ formValues: lwApiSetting }, () => {
        this.validateAll();
      });
    });
  };

  /**
   * @description 画面クローズ
   */
  handleCloseSlide = () => {
    _slideLoader.close(this.props.vw);
  };

  /**
   * @description LINE WORKS API設定画面へリダイレクト
   */
  redirectLwApiSettings = () => {
    this.handleCloseSlide();
  };

  /**
   * @description 編集画面閉じるダイアログ表示
   */
  closeEditView = () => {
    dialogLoader.show(CloseEditView, {
      callback: {
        close: () => {
          dialogLoader.close();
        },
        clear: () => {
          this.handleCloseSlide();
          dialogLoader.close();
        },
      },
    });
  };

  /**
   * @description 値を入力
   */
  handleInput = (name, value) => {
    this.setState(
      {
        formValues: {
          ...this.state.formValues,
          [name]: value,
        },
      },
      () => {
        this.validate(true, name);
      }
    );
  };

  /**
   * @description プライベートキーファイル選択
   */
  selectPrivateKeyFile = async target => {
    if (target.files.length === 0) {
      return;
    }
    //ファイルを取得
    const file = target.files[0];
    // base64コンテンツを抽出
    this.getBase64(file).then(data => {
      // stateに設定
      const formValues = _.cloneDeep(this.state.formValues);
      formValues['private_key'] = data;
      formValues['private_key_file_name'] = file.name;
      this.setState(
        {
          formValues,
        },
        () => {
          this.validate(true, 'private_key');
          this.validate(true, 'private_key_file_name');
        }
      );
    });
  };

  /**
   * @description base64コンテンツを抽出
   */
  getBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        let encoded = reader.result.replace(/^data:(.*;base64,)?/, '');
        if (encoded.length % 4 > 0) {
          encoded += '='.repeat(4 - (encoded.length % 4));
        }
        resolve(encoded);
      };
      reader.onerror = error => reject(error);
    });
  };

  /**
   * @description プライベートキーファイル削除
   */
  deletePrivateKeyFile = () => {
    // stateに設定
    const formValues = _.cloneDeep(this.state.formValues);
    formValues['private_key'] = ''; // クリア
    formValues['private_key_file_name'] = ''; // クリア
    this.setState(
      {
        formValues,
      },
      () => {
        this.validate(true, 'private_key');
        this.validate(true, 'private_key_file_name');
      }
    );
  };

  /**
   * @description SSO有無ラジオボタン変更
   */
  checkSsoUseFlg = selectedValue => {
    // stateに設定
    const formValues = _.cloneDeep(this.state.formValues);
    formValues['sso_use_flg'] = selectedValue === 'true';
    this.setState({
      formValues,
    });
  };

  /**
   * @description 入力値の有効判定（単数項目チェック）
   */
  validate = (e, targetName) => {
    if (!e) {
      return;
    }

    const result = validationByJoi(this.schema, this.state.formValues);

    const validate = _.cloneDeep(this.state.validate);
    const name = _.isEmpty(targetName) ? e.target.name : targetName;
    // バリデートチェック
    if (result.messages[name]) {
      validate.messages[name] = result.messages[name];
    } else {
      delete validate.messages[name];
    }
    validate.invalid = Object.keys(result.messages).length > 0;

    this.setState({ validate });
    if (
      name === 'client_id' ||
      name === 'client_secret' ||
      name === 'service_account' ||
      name === 'private_key' ||
      name === 'private_key_file_name' ||
      name === 'domain_id'
    ) {
      return !(name in validate.messages);
    }
  };

  /**
   * @description 入力値の有効判定（全項目チェック）
   */
  validateAll = () => {
    const result = validationByJoi(this.schema, this.state.formValues);

    const validate = {
      invalid: false, // 入力内容エラー
      messages: this.state.validate.messages, // エラー内容
    };
    // 個別のエラーは入力時にチェックするので、エラーがあるかどうかのみに留める
    if (Object.keys(result.messages).length > 0) {
      validate.invalid = true;
    }

    this.setState({ validate });
    return Object.keys(validate.messages).length === 0;
  };

  /**
   * @description 登録/更新
   */
  submit = async () => {
    if (this.updatingFlag) {
      // 連打の場合は何もしない
      return;
    }
    if (this.validateAll() === false) {
      // バリデーションエラーの場合は何もしない
      return;
    }

    // 連打防止フラグ=>ON
    this.updatingFlag = true;

    // パラメータ設定
    const fieldId = SessionStorageService.getFieldId();
    const params = this.state.formValues;

    // lwApiSettingIdの有無によって呼び出すAPIを判定する
    let response = null;
    if (_.isNull(this.lwApiSettingId)) {
      // 新規登録API呼び出し
      response = await LwApiSettings.create(fieldId, params);
    } else {
      // 更新API呼び出し
      response = await LwApiSettings.update(
        fieldId,
        this.lwApiSettingId,
        params
      );
    }

    if (response.result) {
      // 成功のダイアログを表示
      const updateMsg = messageUtility.getMessage(
        _.isNull(this.lwApiSettingId) ? 'I-000-004' : 'I-000-003'
      );
      dialogLoader.show(FormInformationDialog, {
        title: `LINE WORKS 連携情報${
          _.isNull(this.lwApiSettingId) ? '登録' : '更新'
        }完了`,
        message: `LINE WORKS 連携情報を${updateMsg}`,
        onClose: () => {
          dialogLoader.close();
          this.redirectLwApiSettings();
        },
      });
    } else {
      // エラー処理
      // レスポンスからmessageを取得
      const message =
        response.status === 400 && !_.isNil(response.data.message)
          ? response.data.message
          : '連携に失敗しました。';
      dialogLoader.show(FormInformationDialog, {
        title: `連携失敗`,
        message: `${message}`,
        onClose: () => {
          dialogLoader.close();
        },
      });
    }

    // 連打防止フラグ=>OFF
    this.updatingFlag = false;
  };

  render() {
    return (
      <div className='slide-view show'>
        <Helmet>
          <title>
            {HEADER_CONSTANTS.conference} -
            {`LINE WORKS 連携情報${
              _.isNull(this.lwApiSettingId) ? '登録' : '更新'
            }`}
          </title>
        </Helmet>
        <div className='slide-view__view'>
          <ConferencePageHeader
            loaded='true'
            title={`LINE WORKS 連携情報${
              _.isNull(this.lwApiSettingId) ? '登録' : '更新'
            }`}
            leftComponent={
              <React.Fragment>
                <button
                  className={`btn--effective ${
                    this.state.validate.invalid ? 'btn--disabled' : ''
                  }`}
                  disabled={this.state.validate.invalid}
                  ref={this.submitRef}
                  onClick={async () => {
                    // 重複登録防止
                    if (this.submitRef.current.disabled === false) {
                      await this.events.form.submit();
                    }
                  }}
                >
                  {_.isNull(this.lwApiSettingId) ? '登録' : '更新'}
                </button>
              </React.Fragment>
            }
            onClose={this.closeEditView}
          />
          <div className='slide-view__body LwApiSettingRegister'>
            <div className='LwApiSettingRegister__text'>
              <Description>
                LINE WORKS Developer
                ConsoleからAPI認証に必要な情報を発行して、設定を行ってください。
              </Description>
            </div>
            <LwApiSettingRegisterForm
              events={this.events.form}
              messages={this.state.validate.messages}
              values={this.state.formValues}
            />
          </div>
        </div>
      </div>
    );
  }
}

export { LwApiSettingRegister };

const LwApiSettingRegisterForm = ({ events, messages, values }) => {
  const private_key_validate = () => {
    if (messages.private_key) {
      return messages.private_key;
    } else {
      return messages.private_key_file_name;
    }
  };
  return (
    <FormList isSpace={true}>
      <FormListItem label='Client ID' required='true'>
        <FormElement validate={messages.client_id}>
          <input
            className='form-list__input-large'
            id='client_id'
            type='text'
            name='client_id'
            value={values.client_id}
            maxLength={30}
            onChange={e => {
              events.handleInput('client_id', e.target.value);
            }}
            onBlur={e => {
              events.validate(e);
            }}
          />
        </FormElement>
      </FormListItem>

      <FormListItem label='Client Secret' required='true'>
        <FormElement validate={messages.client_secret}>
          <input
            className='form-list__input-large'
            id='client_secret'
            type='text'
            name='client_secret'
            value={values.client_secret}
            maxLength={30}
            onChange={e => {
              events.handleInput('client_secret', e.target.value);
            }}
            onBlur={e => {
              events.validate(e);
            }}
          />
        </FormElement>
      </FormListItem>

      <FormListItem label='Service Account' required='true'>
        <FormElement validate={messages.service_account}>
          <input
            className='form-list__input-large'
            id='service_account'
            type='text'
            name='service_account'
            value={values.service_account}
            onChange={e => {
              events.handleInput('service_account', e.target.value);
            }}
            onBlur={e => {
              events.validate(e);
            }}
          />
        </FormElement>
      </FormListItem>

      <FormListItem label='Private Key' required='true'>
        <FormElement validate={private_key_validate()}>
          <div className='form-container'>
            {values.private_key && (
              <React.Fragment>
                <span>
                  {values.private_key_file_name || 'Private Keyは設定済です'}
                </span>
                <button
                  className='btn-icon--xs sys-delete form-list__element--btn_delete'
                  onClick={e => events.deletePrivateKeyFile(e)}
                >
                  <TrashIcon className='icon--2_4x form-list__trash-icon' />
                </button>
              </React.Fragment>
            )}
            <div className={values.private_key && 'form-container__item'}>
              <label htmlFor='private_key' className='btn btn--stay input-file'>
                ファイル選択
                <input
                  id='private_key'
                  type='file'
                  name='private_key'
                  accept='.key'
                  onChange={e => events.selectPrivateKeyFile(e.target)}
                  onClick={e => (e.target.value = '')}
                />
              </label>
            </div>
          </div>
        </FormElement>
      </FormListItem>

      <FormListItem label='Domain ID' required='true'>
        <FormElement validate={messages.domain_id}>
          <input
            className='form-list__input-large'
            id='domain_id'
            type='text'
            name='domain_id'
            value={values.domain_id}
            maxLength={20}
            onChange={e => {
              events.handleInput('domain_id', e.target.value);
            }}
            onBlur={e => {
              events.validate(e);
            }}
          />
        </FormElement>
      </FormListItem>

      <FormListItem label='SSO'>
        <FormElement validate={messages.sso_use_flg}>
          <div className='form-list-radio'>
            <div className='form-list-radio__item'>
              <label className='radio'>
                <input
                  type='radio'
                  name='sso_use_flg'
                  value={true}
                  checked={values.sso_use_flg == true}
                  onChange={e => events.checkSsoUseFlg(e.target.value)}
                />
                <RadioButtonIcon className='icon--2x' />
                <span>使用する</span>
              </label>
            </div>
            <div className='form-list-radio__item'>
              <label className='radio'>
                <input
                  type='radio'
                  name='sso_use_flg'
                  value={false}
                  checked={values.sso_use_flg == false}
                  onChange={e => events.checkSsoUseFlg(e.target.value)}
                />
                <RadioButtonIcon className='icon--2x' />
                <span>使用しない</span>
              </label>
            </div>
          </div>
        </FormElement>
      </FormListItem>
    </FormList>
  );
};



// WEBPACK FOOTER //
// ./src/pages/LwApiSettingRegister.jsx